import React, { useState } from 'react';
import {
  Modal,
  Title,
  Text,
  Group,
  Badge,
  Button,
  Stack,
  Image,
  Box,
  Tabs,
  Code,
  ScrollArea,
  ThemeIcon,
  Grid,
  Card,
  Divider,
  List,
  ActionIcon
} from '@mantine/core';
import {
  IconExternalLink,
  IconBrandGithub,
  IconPhoto,
  IconCode,
  IconFileText,
  IconX,
  IconChevronLeft,
  IconChevronRight
} from '@tabler/icons-react';
import ImageModal from './ImageModal';

interface ProjectData {
  title: string;
  description: string;
  longDescription: string;
  features: string[];
  technologies: string[];
  type: string;
  color: string;
  gradient: { from: string; to: string };
  images?: string[];
  codeSnippets?: Array<{
    title: string;
    language: string;
    code: string;
  }>;
  liveUrl?: string;
  githubUrl?: string;
  achievements?: string[];
  challenges?: string[];
  futureEnhancements?: string[];
}

interface ProjectModalProps {
  opened: boolean;
  onClose: () => void;
  project: ProjectData | null;
}

export const ProjectModal: React.FC<ProjectModalProps> = ({ opened, onClose, project }) => {
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [imageModalOpened, setImageModalOpened] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{ src: string; alt: string } | null>(null);

  const handleImageClick = (imageSrc: string, imageAlt: string) => {
    setSelectedImage({ src: imageSrc, alt: imageAlt });
    setImageModalOpened(true);
  };

  if (!project) return null;

  return (
    <>
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      title={
        <Group>
          <ThemeIcon
            size={40}
            radius="md"
            variant="gradient"
            gradient={project.gradient}
          >
            <IconFileText size={24} />
          </ThemeIcon>
          <Box>
            <Title order={3}>{project.title}</Title>
            <Badge variant="gradient" gradient={project.gradient} size="sm">
              {project.type}
            </Badge>
          </Box>
        </Group>
      }
      styles={{
        content: {
          background: 'linear-gradient(145deg, #1a1a1a 0%, #2a2a2a 100%)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          maxHeight: '90vh'
        },
        header: {
          background: 'transparent',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        },
        body: {
          padding: 0
        }
      }}
    >
      <ScrollArea h={600} p="md">
        <Tabs value={activeTab} onChange={setActiveTab as any}>
          <Tabs.List grow>
            <Tabs.Tab value="overview" leftSection={<IconFileText size={16} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="media" leftSection={<IconPhoto size={16} />}>
              Media
            </Tabs.Tab>
            <Tabs.Tab value="details" leftSection={<IconCode size={16} />}>
              Details
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="md">
            <Stack gap="lg">
              <Text size="lg" lh={1.6}>
                {project.longDescription}
              </Text>

              <Group justify="space-between">
                {project.liveUrl && (
                  <Button
                    variant="gradient"
                    gradient={project.gradient}
                    leftSection={<IconExternalLink size={16} />}
                    component="a"
                    href={project.liveUrl}
                    target="_blank"
                  >
                    View Live Project
                  </Button>
                )}
                {project.githubUrl && (
                  <Button
                    variant="outline"
                    leftSection={<IconBrandGithub size={16} />}
                    component="a"
                    href={project.githubUrl}
                    target="_blank"
                  >
                    View Source
                  </Button>
                )}
              </Group>

              <Divider />

              <Box>
                <Title order={4} mb="md">Key Features</Title>
                <List>
                  {project.features.map((feature, index) => (
                    <List.Item key={index}>{feature}</List.Item>
                  ))}
                </List>
              </Box>

              <Box>
                <Title order={4} mb="md">Technologies Used</Title>
                <Group gap="xs">
                  {project.technologies.map((tech, index) => (
                    <Badge
                      key={index}
                      variant="light"
                      style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: 'white'
                      }}
                    >
                      {tech}
                    </Badge>
                  ))}
                </Group>
              </Box>

              {project.achievements && (
                <Box>
                  <Title order={4} mb="md">Key Achievements</Title>
                  <List>
                    {project.achievements.map((achievement, index) => (
                      <List.Item key={index}>{achievement}</List.Item>
                    ))}
                  </List>
                </Box>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="media" pt="md">
            <Stack gap="lg">
              {project.images && project.images.length > 0 && (
                <Box>
                  <Title order={4} mb="md">Screenshots</Title>
                  <Grid>
                    {project.images.map((image, index) => (
                      <Grid.Col key={index} span={6}>
                        <Card 
                          p="xs" 
                          style={{ 
                            background: 'rgba(255, 255, 255, 0.05)',
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                          }}
                          onClick={() => handleImageClick(image, `${project.title} screenshot ${index + 1}`)}
                          onMouseOver={(e) => {
                            e.currentTarget.style.transform = 'scale(1.02)';
                            e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.3)';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <Image
                            src={image}
                            alt={`${project.title} screenshot ${index + 1}`}
                            radius="md"
                            fallbackSrc="https://via.placeholder.com/400x300/1a1a1a/ffffff?text=Screenshot+Placeholder"
                          />
                          <Box
                            style={{
                              position: 'absolute',
                              top: '8px',
                              right: '8px',
                              background: 'rgba(0, 0, 0, 0.6)',
                              borderRadius: '4px',
                              padding: '4px 8px'
                            }}
                          >
                            <Text size="xs" c="white">
                              Click to expand
                            </Text>
                          </Box>
                        </Card>
                      </Grid.Col>
                    ))}
                  </Grid>
                </Box>
              )}

              {(!project.images || project.images.length === 0) && (
                <Box ta="center" py="xl">
                  <Text c="dimmed">
                    Screenshots will be displayed here. Add screenshots to showcase your project.
                  </Text>
                </Box>
              )}
            </Stack>
          </Tabs.Panel>



          <Tabs.Panel value="details" pt="md">
            <Stack gap="lg">
              {project.challenges && (
                <Box>
                  <Title order={4} mb="md">Technical Challenges</Title>
                  <List>
                    {project.challenges.map((challenge, index) => (
                      <List.Item key={index}>{challenge}</List.Item>
                    ))}
                  </List>
                </Box>
              )}

              {project.futureEnhancements && (
                <Box>
                  <Title order={4} mb="md">Future Enhancements</Title>
                  <List>
                    {project.futureEnhancements.map((enhancement, index) => (
                      <List.Item key={index}>{enhancement}</List.Item>
                    ))}
                  </List>
                </Box>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </ScrollArea>
    </Modal>

    {/* Image Modal */}
    {selectedImage && (
      <ImageModal
        opened={imageModalOpened}
        onClose={() => setImageModalOpened(false)}
        src={selectedImage.src}
        alt={selectedImage.alt}
      />
    )}
  </>
  );
}; 